/**
 * What a TLD needs to be sold, and why a registry cannot answer (subsystems/registrar/facade/adapters/admin-api/src/admin/tld_setup.rs):
 *
 * - `RegistryProblemCallout`: the reason of a failed check / quote in words (not configured,
 *   login refused, provider error code, timeout, extension not offered) with links to the
 *   connection page and the TLD page;
 * - `PublicationText`: who publishes a zone's RDAP / WHOIS ("Do WebNIC công bố"), instead of a
 *   struck-through label;
 * - `ReadinessBadge`: the one-line state of a zone in the TLD list;
 * - `TldReadinessPanel`: the checklist on the TLD page, with its one-click fixes and the
 *   "Thử kiểm tra một tên" check.
 */

import { CheckCircle2, CircleAlert, CircleDashed, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { Publisher, ReadinessItem, ReadinessState, RegistryCheckResult, RegistryProblem, TldReadinessSummary, ZoneConnectionRef } from '../../api/areas/registry';
import type { WriteOptions } from '../../api/types';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, Field, Section } from '../../components/blocks';
import { ProgressRow } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { useAddonName } from '../services/shared';

type TFn = (key: string, o?: Record<string, unknown>) => string;

export const connectionUrl = (c: Pick<ZoneConnectionRef, 'code'>) => `/settings/registries/${encodeURIComponent(c.code)}`;
export const tldUrl = (id: string, tab?: string) => `/registry/tlds/${encodeURIComponent(id)}${tab ? `?tab=${tab}` : ''}`;

const linkClass = 'font-medium text-primary underline-offset-4 hover:underline';
const OPS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE'] as const;

/** A registry failure explained, with where to fix it. */
export function RegistryProblemCallout({ problem, tldId, title }: { problem: Partial<Omit<RegistryProblem, 'reason'>> & { reason?: string | null }; tldId?: string; title?: ReactNode }) {
  const { t, i18n } = useTranslation('registry');
  const tr = t as unknown as TFn;
  const reason = problem.reason ?? 'other';
  const heading = title ?? tr(`problem.reason.${reason}`, { defaultValue: tr('problem.reason.other') });
  const detail = i18n.language === 'vi' ? problem.detail : (problem.detail_en ?? problem.detail);
  const tld = tldId ?? problem.tld_id;
  return (
    <Callout tone={reason === 'REGISTRY_TIMEOUT' ? 'pending' : 'warn'} title={heading}>
      <div className="grid gap-1.5">
        {problem.missing ? <p className="font-medium">{tr(`problem.missing.${problem.missing}`, { defaultValue: problem.missing })}</p> : null}
        {detail ? <p>{detail}</p> : null}
        {problem.provider_code || problem.provider_message ? (
          <p className="font-mono text-xs text-muted-foreground">{t('problem.provider', { code: problem.provider_code ?? '', message: problem.provider_message ?? '' })}</p>
        ) : null}
        {reason === 'REGISTRY_AUTH_FAILED' ? <p className="text-xs text-muted-foreground">{t('problem.hintAuth')}</p> : null}
        {problem.registry || tld ? (
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            {problem.registry ? (
              <Link to={connectionUrl(problem.registry)} className={linkClass}>
                {t('problem.openConnection', { name: problem.registry.name })}
              </Link>
            ) : null}
            {tld ? (
              <Link to={tldUrl(tld)} className={linkClass}>
                {t('problem.openTld')}
              </Link>
            ) : null}
          </p>
        ) : null}
      </div>
    </Callout>
  );
}

/** Who publishes a zone's RDAP / WHOIS, in words. */
export function PublicationText({ publisher, connection }: { publisher: Publisher; connection?: Pick<ZoneConnectionRef, 'name'> | null }) {
  const { t } = useTranslation('registry');
  const tr = t as unknown as TFn;
  const name = connection?.name ?? '';
  const tone: Tone = publisher === 'US' ? 'ok' : publisher === 'OFF' || publisher === 'PARTIAL' ? 'warn' : 'neutral';
  return (
    <Badge tone={tone} icon={false} title={tr(`publication.hint.${publisher}`, { name })}>
      {tr(`publication.${publisher}`, { name })}
    </Badge>
  );
}

/** A zone's publisher from its flags and channel when the API gave no summary. */
export function publisherOf(rdap: boolean, whois: boolean, kind: string | undefined, tldClass: string): Publisher {
  if (rdap && whois) return 'US';
  if (kind === 'UPSTREAM_REGISTRAR') return 'UPSTREAM';
  if (tldClass === 'VN') return 'REGISTRY';
  return rdap || whois ? 'PARTIAL' : 'OFF';
}

/** The one-line state of a zone: ready, or what is missing. */
export function ReadinessBadge({ summary, to }: { summary: TldReadinessSummary | null | undefined; to?: string }) {
  const { t } = useTranslation('registry');
  const tr = t as unknown as TFn;
  if (!summary) return <span className="text-muted-foreground">—</span>;
  const body = summary.ready ? (
    <Badge tone="ok">{t('readiness.ready')}</Badge>
  ) : (
    <span className="grid gap-0.5">
      <Badge tone="warn">{t('readiness.notReady')}</Badge>
      <span className="text-xs text-muted-foreground">{t('readiness.missingList', { list: summary.missing.map((c) => tr(`readiness.short.${c}`)).join(', ') })}</span>
    </span>
  );
  return to ? (
    <Link to={to} className="inline-grid">
      {body}
    </Link>
  ) : (
    body
  );
}

const STATE_ICON: Record<ReadinessState, typeof CheckCircle2> = { OK: CheckCircle2, MISSING: CircleAlert, WARN: CircleAlert, UNKNOWN: CircleDashed };
const STATE_TONE: Record<ReadinessState, string> = { OK: 'text-success', MISSING: 'text-destructive', WARN: 'text-warning', UNKNOWN: 'text-muted-foreground' };

/** The sentence under a checklist item; add-ons by their catalogue name. */
function itemText(item: ReadinessItem, tr: TFn, addonName: (code: string) => string): ReactNode {
  const d = item.data;
  const conn = d.connection;
  switch (item.code) {
    case 'CONNECTION':
      return item.state === 'OK' && conn ? tr('readiness.connectionOk', { name: conn.name, source: tr(`readiness.source.${conn.source}`) }) : null;
    case 'LOGIN_TEST':
      if (!d.outcome) return tr('readiness.loginNever', { name: conn?.name ?? '' });
      return `${tr(d.outcome === 'OK' ? 'readiness.loginOk' : 'readiness.loginFailed', { when: d.finished_at ? formatDateTime(d.finished_at) : '' })}${
        d.outcome !== 'OK' && typeof d.missing === 'string' ? ` ${tr(`problem.missing.${d.missing}`, { defaultValue: d.missing })}` : ''
      }`;
    case 'REGISTRY_COST': {
      if (item.state === 'MISSING') return tr('readiness.noCost');
      const costs = (d.costs ?? []).map((c) => `${tr(`setup.op.${c.operation}`)} ${c.amount} ${c.currency}`).join(' · ');
      const missing = d.missing_operations?.length ? ` ${tr('readiness.costsMissingOps', { ops: d.missing_operations.map((o) => tr(`setup.op.${o}`)).join(', ') })}` : '';
      // Set before the zone moved to its current channel: the previous channel's price.
      const stale = d.missing === 'COST_NOT_CHANNEL' ? ` ${tr('readiness.costNotChannel', { name: conn?.name ?? '' })}` : '';
      return `${costs}${missing}${stale}`;
    }
    case 'SALE_PRICE': {
      const books = d.books ?? [];
      if (!books.length) return tr('readiness.noBook');
      if (item.state === 'MISSING') return tr('readiness.saleMissing', { books: books.filter((b) => !b.register).map((b) => b.code).join(', ') });
      return `${tr('readiness.saleOk', { books: books.map((b) => b.code).join(', ') })}${item.state === 'WARN' ? ` ${tr('readiness.renewMissing')}` : ''}`;
    }
    case 'FX_RATE': {
      const pairs = ((d.missing as unknown as { from: string; to: string }[] | null | undefined) ?? []).map((p) => `${p.from} → ${p.to}`).join(', ');
      return pairs ? tr('readiness.fxMissing', { pairs }) : null;
    }
    case 'ADDONS': {
      if (d.unpriced?.length) return tr('readiness.addonsUnpriced', { list: d.unpriced.map(addonName).join(', ') });
      if (d.missing === 'NOT_DECIDED') return tr('readiness.addonsNotDecided', { list: (d.suggested ?? []).map((s) => addonName(s.code)).join(', ') });
      if (d.included_upstream?.length) return tr('readiness.addonsIncluded', { list: d.included_upstream.map(addonName).join(', ') });
      return d.offered?.length ? tr('readiness.addonsOk', { count: d.offered.length }) : tr('readiness.addonsNone');
    }
    case 'PUBLICATION':
      return d.publisher ? tr(`publication.hint.${d.publisher}`, { name: conn?.name ?? '' }) : null;
    case 'AVAILABILITY':
      return tr('readiness.availabilityHint');
    default:
      return null;
  }
}

/** "Đặt giá bán": a margin or a fixed price for every operation in the default tier's books. */
function SaleForm({ close, changed, tldId, books }: DialogBodyProps & { tldId: string; books: { id: string; code: string; currency: string }[] }) {
  const { t } = useTranslation('registry');
  const [mode, setMode] = useState<'MARKUP_PERCENT' | 'FIXED_PRICE'>('MARKUP_PERCENT');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState<string>(t('readiness.reasonSale'));
  const w = useWrite(async (_: null, opts: WriteOptions) => {
    for (const b of books) {
      await api.publishTldPrices(
        b.id,
        { tld_id: tldId, reason: reason.trim(), items: OPS.map((operation) => ({ operation, rule_type: mode, value: value.trim() })) },
        { ...opts, idempotencyKey: opts.idempotencyKey ? `${opts.idempotencyKey}-${b.id}` : undefined },
      );
    }
    return true;
  });
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(null).then((r) => {
          if (r) {
            changed();
            close();
          }
        });
      }}
    >
      <p className="text-sm text-muted-foreground">{t('readiness.books', { books: books.map((b) => `${b.code} (${b.currency})`).join(', ') })}</p>
      <Field label={t('readiness.fix.SET_SALE_PRICE')}>
        <select className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
          <option value="MARKUP_PERCENT">{t('readiness.margin')}</option>
          <option value="FIXED_PRICE">{t('readiness.fixed')}</option>
        </select>
      </Field>
      <Field label={t('readiness.value')}>
        <Input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" required className="w-40" />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!value.trim() || !reason.trim()} submit={t('readiness.fix.SET_SALE_PRICE')} onClose={close} />
    </form>
  );
}

/** "Thử kiểm tra một tên": asks the registry and shows the detailed reason. */
export function TestCheck({ tldId, zone }: { tldId: string; zone: string }) {
  const { t } = useTranslation('registry');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RegistryCheckResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.testTldCheck(tldId, { name: name.trim() || undefined }));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-2">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <Field label={t('readiness.testName')}>
          <div className="flex items-center gap-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('readiness.testPlaceholder')} className="w-56 font-mono" autoComplete="off" />
            <span className="font-mono text-sm text-muted-foreground">.{zone}</span>
          </div>
        </Field>
        <Button type="submit" size="sm" variant="outline" disabled={busy}>
          <Search /> {t('readiness.test')}
        </Button>
      </form>
      {error ? <DataError error={error} /> : null}
      {result ? (
        result.available === null ? (
          <RegistryProblemCallout problem={result} />
        ) : result.available ? (
          <Callout tone="ok">{t('readiness.testAvailable', { name: result.unicode ?? result.name })}</Callout>
        ) : (
          <Callout tone="info">{t('readiness.testTaken', { name: result.unicode ?? result.name })}</Callout>
        )
      ) : null}
    </div>
  );
}

/** A fix applied with one click (reason prefilled, audited as usual). */
function OneClick({ label, run, onDone }: { label: string; run: (opts: WriteOptions) => Promise<unknown>; onDone: () => void }) {
  const w = useWrite((_: null, opts: WriteOptions) => run(opts));
  return (
    <span className="grid justify-items-end gap-1">
      <Button size="xs" disabled={w.busy} onClick={() => void w.run(null).then((r) => r && onDone())}>
        {label}
      </Button>
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
    </span>
  );
}

/** The checklist of one zone, with its fixes. */
export function TldReadinessPanel({ tldId, zone, onChanged }: { tldId: string; zone: string; onChanged?: () => void }) {
  const { t } = useTranslation('registry');
  const tr = t as unknown as TFn;
  const result = useApi((signal) => api.getTldReadiness(tldId, { signal }), [tldId]);
  const addonName = useAddonName();
  const [testing, setTesting] = useState(false);
  const reload = () => {
    result.reload();
    onChanged?.();
  };
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  const r = result.data;
  const saleBooks = (r.items.find((i) => i.code === 'SALE_PRICE')?.data.books ?? []).map((b) => ({ id: b.id, code: b.code, currency: b.currency }));
  const fix = (item: ReadinessItem): ReactNode => {
    if (item.state === 'OK' && item.code !== 'AVAILABILITY') return null;
    const conn = item.data.connection ?? r.channel;
    switch (item.fix) {
      case 'OPEN_CONNECTION':
        return (
          <Link to={connectionUrl(conn)} className={linkClass}>
            {t('readiness.fix.OPEN_CONNECTION')}
          </Link>
        );
      case 'OPEN_PRICING':
        return (
          <Link to={tldUrl(tldId, 'pricing')} className={linkClass}>
            {t('readiness.fix.OPEN_PRICING')}
          </Link>
        );
      case 'SET_SALE_PRICE':
        return saleBooks.length ? (
          <ActionDialog label={t('readiness.fix.SET_SALE_PRICE')} title={t('readiness.saleTitle', { zone })} onChanged={reload}>
            {(p) => <SaleForm {...p} tldId={tldId} books={saleBooks} />}
          </ActionDialog>
        ) : (
          <Link to="/products?tab=pricing" className={linkClass}>
            {t('readiness.fix.OPEN_PRICING')}
          </Link>
        );
      case 'APPLY_DEFAULT_ADDONS':
        return <OneClick label={t('readiness.fix.APPLY_DEFAULT_ADDONS')} run={(opts) => api.applyTldAddonDefaults(tldId, { reason: t('readiness.reasonDefaults') }, opts)} onDone={reload} />;
      case 'ENABLE_PUBLICATION':
        return (
          <OneClick
            label={t('readiness.fix.ENABLE_PUBLICATION')}
            run={(opts) => api.setTldPublication(tldId, { rdap_enabled: true, whois_enabled: true, reason: t('readiness.reasonPublication') }, opts)}
            onDone={reload}
          />
        );
      case 'TEST_CHECK':
        return (
          <Button size="xs" variant="outline" onClick={() => setTesting((v) => !v)}>
            <Search /> {t('readiness.fix.TEST_CHECK')}
          </Button>
        );
      default:
        return null;
    }
  };
  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('readiness.title')} {r.ready ? <Badge tone="ok">{t('readiness.ready')}</Badge> : <Badge tone="warn">{t('readiness.notReady')}</Badge>}
        </span>
      }
    >
      <p className="mb-3 text-sm text-muted-foreground">{t('readiness.intro', { zone })}</p>
      <div className="mb-4">
        <ProgressRow
          label={t('hub.readinessProgress')}
          value={r.items.filter((i) => i.state === 'OK').length}
          total={r.items.length}
          tone={r.ready ? 'ok' : 'warn'}
        />
      </div>
      <ul className="grid gap-2">
        {r.items.map((item) => {
          const Icon = STATE_ICON[item.state];
          const problem = item.code === 'CONNECTION' && item.state !== 'OK' ? item.data : null;
          return (
            <li key={item.code} className="grid gap-1.5 rounded-lg border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className="flex items-start gap-2">
                  <Icon className={`mt-0.5 size-4 shrink-0 ${STATE_TONE[item.state]}`} aria-hidden="true" />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">
                      {tr(`readiness.item.${item.code}`)} <span className="text-xs font-normal text-muted-foreground">· {tr(`readiness.state.${item.state}`)}</span>
                    </span>
                    {item.code === 'PUBLICATION' && item.data.publisher ? (
                      <span>
                        <PublicationText publisher={item.data.publisher} connection={item.data.connection} />
                      </span>
                    ) : null}
                    <span className="text-xs text-muted-foreground">{itemText(item, tr, addonName)}</span>
                  </span>
                </span>
                <span className="flex items-center gap-2">{fix(item)}</span>
              </div>
              {problem?.reason ? (
                <RegistryProblemCallout problem={{ reason: problem.reason, missing: problem.missing ?? null, detail: problem.detail, detail_en: problem.detail_en, provider_code: problem.provider_code, provider_message: problem.provider_message }} />
              ) : problem?.missing ? (
                <p className="text-xs">{tr(`problem.missing.${problem.missing}`, { defaultValue: problem.missing })}</p>
              ) : null}
              {item.code === 'AVAILABILITY' && testing ? <TestCheck tldId={tldId} zone={zone} /> : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
